import { useEffect, useState } from "react";
import { FileDown, Menu, X } from "lucide-react";
import { Logo } from "./Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PDF_URL = `${BACKEND_URL}/api/capability-statement.pdf`;

const NAV = [
  { label: "Capabilities", href: "#capabilities", testid: "nav-capabilities-link" },
  { label: "Government Compliance", href: "#compliance", testid: "nav-compliance-link" },
  { label: "Teaming & Sourcing", href: "#teaming", testid: "nav-teaming-link" },
  { label: "Contact", href: "#contact", testid: "nav-contact-link" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50" data-testid="site-header">
      <div className="bg-[#14171A] border-b border-slate-800" data-testid="credentials-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-8 sm:h-9 flex items-center justify-center sm:justify-between gap-4">
          <p className="font-mono text-[10px] sm:text-xs tracking-[0.18em] uppercase text-slate-400 truncate">
            <span className="text-amber-500">&#9670;</span> NCAGE 01GUU
            <span className="md:hidden text-slate-600"> &nbsp;|&nbsp; </span>
            <span className="md:hidden"><span className="text-amber-500">&#9670;</span> SAM.gov Vendor</span>
            <span className="hidden md:inline text-slate-600"> &nbsp;|&nbsp; </span>
            <span className="hidden md:inline"><span className="text-amber-500">&#9670;</span> SAM.gov Registered Foreign Vendor</span>
            <span className="hidden lg:inline text-slate-600"> &nbsp;|&nbsp; </span>
            <span className="hidden lg:inline"><span className="text-amber-500">&#9670;</span> UEI FJNKCGJV1QQ6</span>
          </p>
          <p className="hidden sm:block font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500">DEF-COIN.COM</p>
        </div>
      </div>

      <nav
        className={`border-b border-slate-800 transition-colors duration-300 ${
          scrolled ? "bg-slate-950/80 backdrop-blur-xl" : "bg-slate-950/40 backdrop-blur-md"
        }`}
        data-testid="main-nav"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 lg:h-16 flex items-center justify-between">
          <a href="#top" className="group" data-testid="nav-logo-link" onClick={() => setOpen(false)} aria-label="DEFCOIN Ltd. home">
            <Logo markClass="h-9 w-9 sm:h-10 sm:w-10" id="nav-logo" testid="nav-logo" />
          </a>

          <div className="hidden lg:flex items-center gap-8">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={item.testid}
                className="font-mono text-xs tracking-[0.15em] uppercase text-slate-400 hover:text-amber-500 transition-colors duration-200"
              >
                {item.label}
              </a>
            ))}
            <a
              href={PDF_URL}
              download
              data-testid="download-capability-btn"
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-mono text-xs tracking-[0.12em] uppercase px-4 py-2.5 border border-blue-600 hover:border-amber-500 transition-colors duration-200"
            >
              <FileDown className="w-4 h-4" />
              Capability Statement
            </a>
          </div>

          <button
            className="lg:hidden -mr-2 w-11 h-11 flex items-center justify-center text-slate-300 hover:text-amber-500 transition-colors"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {open && (
        <div
          className="lg:hidden fixed inset-x-0 top-[88px] sm:top-[92px] bottom-0 bg-[#0D0F12] px-4 sm:px-6 py-4 flex flex-col overflow-y-auto"
          data-testid="mobile-menu"
        >
          <nav className="divide-y divide-slate-800/70">
            {NAV.map((item, i) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                data-testid={`${item.testid}-mobile`}
                className="flex items-center justify-between min-h-[56px] font-mono text-sm tracking-[0.15em] uppercase text-slate-200 active:text-amber-500"
              >
                {item.label}
                <span className="font-mono text-[10px] text-slate-600 tracking-[0.2em]">0{i + 1}</span>
              </a>
            ))}
          </nav>
          <a
            href={PDF_URL}
            download
            onClick={() => setOpen(false)}
            data-testid="download-capability-btn-mobile"
            className="mt-6 flex items-center justify-center gap-2 min-h-[52px] bg-[#4A5D4E] text-white font-mono text-xs tracking-[0.12em] uppercase px-4 w-full"
          >
            <FileDown className="w-4 h-4" />
            Download Capability Statement
          </a>
          <div className="mt-auto pt-8 pb-6 space-y-1.5">
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Defense Desk</p>
            <a href="mailto:contracts@def-coin.com" className="block font-mono text-sm text-amber-600" data-testid="mobile-menu-email-link">
              contracts@def-coin.com
            </a>
            <a href="tel:+359876065815" className="block font-mono text-sm text-slate-300" data-testid="mobile-menu-phone-link">
              +359 87 606 5815
            </a>
            <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-slate-600">Sofia, BG // NCAGE 01GUU</p>
          </div>
        </div>
      )}
    </header>
  );
}
